import { useState, useEffect } from "react";
import Cross from "../../assets/icons/Cross";
import DefaultButton from "../default-button/DefaultButton";
import ArrowLeft from "../../assets/icons/ArrowLeft";
import Tick from "../../assets/icons/Tick";
import DatePicker from "react-datepicker";
import { registerLocale } from "react-datepicker";
import { ru } from "date-fns/locale";
import "react-datepicker/dist/react-datepicker.css";

// Регистрируем русскую локаль для DatePicker
registerLocale("ru", ru);

interface DateRangeModalProps {
    isOpen: boolean;
    onClose: () => void;
    onApply: (dateFrom: Date | null, dateTo: Date | null) => void;
    dateFrom?: Date | null;
    dateTo?: Date | null;
}

export default function DateRangeModal({
    isOpen,
    onClose,
    onApply,
    dateFrom: initialDateFrom,
    dateTo: initialDateTo,
}: DateRangeModalProps) {
    const [startDate, setStartDate] = useState<Date | null>(initialDateFrom || null);
    const [endDate, setEndDate] = useState<Date | null>(initialDateTo || null);

    // Синхронизируем состояние с пропсами при открытии модалки
    useEffect(() => {
        if (isOpen) {
            setStartDate(initialDateFrom || null);
            setEndDate(initialDateTo || null);
        }
    }, [isOpen, initialDateFrom, initialDateTo]);

    const handleApply = () => {
        onApply(startDate, endDate);
        onClose();
    };

    const handleClear = () => {
        setStartDate(null);
        setEndDate(null);
        onApply(null, null);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="w-full max-w-2xl bg-white rounded-xl p-4 md:p-6 lg:p-8 flex flex-col max-h-[95vh] overflow-y-auto relative border-2 border-dashed border-blue-200">
                {/* Header */}
                <div className="flex items-center justify-between mb-4 md:mb-6 flex-shrink-0">
                    <p className="text-black font-normal text-xl md:text-2xl">
                        Указать даты
                    </p>
                    <button
                        onClick={onClose}
                        className="flex items-center justify-center cursor-pointer hover:bg-gray-100 rounded p-1"
                    >
                        <Cross additionalStyle="text-[#5F6B88]" />
                    </button>
                </div>

                {/* Calendar */}
                <div className="flex-grow flex justify-center items-start mb-4 md:mb-6 overflow-x-auto">
                    <DatePicker
                        selected={startDate}
                        onChange={(dates) => {
                            const [start, end] = dates as [Date | null, Date | null];
                            setStartDate(start);
                            setEndDate(end);
                        }}
                        startDate={startDate}
                        endDate={endDate}
                        selectsRange
                        inline
                        monthsShown={2}
                        locale="ru"
                        dateFormat="dd/MM/yyyy"
                        calendarClassName="date-range-calendar"
                        showMonthDropdown
                        showYearDropdown
                        dropdownMode="select"
                        fixedHeight
                    />
                </div>

                {/* Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 mt-2 flex-shrink-0">
                    <DefaultButton
                        buttonText="Вернуться"
                        additionalStyle="bg-[#5F6B88] text-white px-6 md:px-8 py-3 text-nowrap w-full sm:w-auto order-2 sm:order-1"
                        Icon={ArrowLeft}
                        onClick={onClose}
                    />
                    <div className="flex gap-2 flex-1 order-1 sm:order-2">
                        <DefaultButton
                            buttonText="Очистить"
                            additionalStyle="bg-valley-gray text-white px-4 py-3 text-nowrap flex-shrink-0"
                            onClick={handleClear}
                        />
                        <DefaultButton
                            buttonText="Применить"
                            additionalStyle="bg-valley-dark-green text-white px-4 py-3 text-nowrap flex-1"
                            Icon={Tick}
                            onClick={handleApply}
                        />
                    </div>
                </div>
            </div>

            <style>{`
                .react-datepicker {
                    border: none !important;
                    box-shadow: none !important;
                    font-family: inherit;
                    display: flex;
                    gap: 1rem;
                }
                .react-datepicker__header {
                    background-color: white;
                    border-bottom: 1px solid #e5e7eb;
                    padding-top: 0.75rem;
                    padding-bottom: 0.5rem;
                }
                .react-datepicker__month-container {
                    margin: 0;
                    float: none;
                }
                .react-datepicker__month {
                    margin: 0.5rem;
                }
                .react-datepicker__day--selected {
                    background-color: #0068FF;
                    color: white;
                }
                .react-datepicker__day--in-selecting-range {
                    background-color: #E3F2FD;
                    color: #0068FF;
                }
                .react-datepicker__day--range-start,
                .react-datepicker__day--range-end {
                    background-color: #0068FF !important;
                    color: white !important;
                    border-radius: 50% !important;
                    font-weight: 600;
                }
                .react-datepicker__day--in-range {
                    background-color: #E3F2FD;
                    color: #0068FF;
                    border-radius: 0;
                }
                .react-datepicker__day--in-range:not(.react-datepicker__day--range-start):not(.react-datepicker__day--range-end) {
                    border: 2px dashed #0068FF;
                }
                .react-datepicker__day:hover:not(.react-datepicker__day--disabled):not(.react-datepicker__day--selected):not(.react-datepicker__day--in-range) {
                    border-radius: 50%;
                    background-color: #f3f4f6;
                }
                .react-datepicker__day--keyboard-selected {
                    background-color: #0068FF;
                    color: white;
                    border-radius: 50%;
                }
                .react-datepicker__navigation {
                    top: 1rem;
                }
                .react-datepicker__current-month {
                    font-weight: 500;
                    margin-bottom: 0.5rem;
                    font-size: 0.944rem;
                    text-transform: capitalize;
                }
                .react-datepicker__day-name {
                    color: #6b7280;
                    font-weight: 500;
                    width: 2rem;
                    line-height: 2rem;
                    margin: 0.166rem;
                }
                .react-datepicker__day {
                    margin: 0.166rem;
                    width: 2rem;
                    line-height: 2rem;
                }
                .react-datepicker__month-dropdown,
                .react-datepicker__year-dropdown {
                    background-color: white;
                    border: 1px solid #e5e7eb;
                    border-radius: 0.375rem;
                }
                @media (max-width: 768px) {
                    .react-datepicker {
                        flex-direction: column;
                    }
                    .react-datepicker__month-container {
                        width: 100%;
                    }
                }
            `}</style>
        </div>
    );
}
